/**
 * Showreel die uitklapt naar een speler midden in beeld.
 *
 * Er gaat geen tweede venster open: het kaartje uit de hero reist zelf naar het kader in
 * de lightbox. Dat is wat GSAP Flip doet -- eerst meten waar iets staat, dan verplaatsen,
 * en Flip animeert het verschil weg. Daardoor blijft het dezelfde video die groter wordt
 * in plaats van een nieuwe die ergens anders begint.
 *
 * Twee dingen die minder voor de hand liggen:
 *
 * - Boven de hero hangt een parallax-transform, en binnen een transform rekent
 *   position: fixed tegen dát blok in plaats van tegen het venster. De lightbox én de
 *   speler moeten er dus uit; ze verhuizen naar de body en bij het sluiten weer terug.
 *
 * - Het kader waar de speler naartoe gaat, wordt gemeten aan de dichte kaart. De
 *   randen eromheen (padding, het regeltje eronder) schalen niet mee met de breedte,
 *   dus die worden apart opgeteld -- anders blijft de film een stuk kleiner dan het
 *   scherm toelaat.
 */

import gsap from 'gsap';
import Flip from 'gsap/Flip';

import { setupPlayer } from './player';
import { lockScroll, unlockScroll } from './scroll-lock';

gsap.registerPlugin(Flip);

const DURATION = 0.9;
const EASE = 'expo.inOut';

export default function initShowreel() {
  document.querySelectorAll('[data-showreel]').forEach(setup);
}

function setup(root) {
  const slot = root.querySelector('[data-showreel-slot]');
  const stage = root.querySelector('[data-showreel-stage]');
  const frame = root.querySelector('[data-showreel-frame]');
  const media = root.querySelector('[data-showreel-media]');
  const source = root.querySelector('[data-showreel-source]');
  const trigger = root.querySelector('[data-showreel-open]');
  const lightbox = root.querySelector('[data-showreel-lightbox]');

  if (! slot || ! stage || ! frame || ! media || ! source || ! trigger || ! lightbox) {
    return;
  }

  const safearea = lightbox.querySelector('[data-showreel-safearea]');
  const close = lightbox.querySelector('[data-showreel-close]');

  if (! safearea || ! close) {
    return;
  }

  // Uit de parallax vandaan, anders is position: fixed niet fixed.
  document.body.appendChild(lightbox);

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const preview = root.querySelector('.showreel__preview');

  let isOpen = false;
  let inlineStyle = '';
  // Het handvat van de speler, als de film er een met eigen knoppen is (Vimeo of een
  // eigen bestand). Bij YouTube blijft dit null en speelt de iframe zichzelf af.
  let player = null;

  trigger.addEventListener('click', open);
  close.addEventListener('click', () => shut());

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen) {
      shut();
    }
  });

  window.addEventListener('resize', () => {
    if (isOpen) {
      place();
    }
  });

  /**
   * Het kader waar de speler in past: zo groot mogelijk binnen de veilige zone, maar met
   * de randen van de kaart erbij opgeteld. Die randen zijn een vast aantal pixels en
   * groeien dus niet mee -- ze uit de verhouding van de dichte kaart afleiden zou de film
   * op een groot scherm onnodig klein houden.
   */
  function box() {
    const bounds = safearea.getBoundingClientRect();
    const outer = stage.getBoundingClientRect();
    const inner = frame.getBoundingClientRect();

    const chromeX = Math.max(0, outer.width - inner.width);
    const chromeY = Math.max(0, outer.height - inner.height);
    const ratio = inner.height > 0 ? inner.width / inner.height : 16 / 9;

    let width = bounds.width;
    let height = (width - chromeX) / ratio + chromeY;

    if (height > bounds.height) {
      height = bounds.height;
      width = (height - chromeY) * ratio + chromeX;
    }

    return {
      left: bounds.left + (bounds.width - width) / 2,
      top: bounds.top + (bounds.height - height) / 2,
      width,
      height,
    };
  }

  function place() {
    gsap.set(stage, Object.assign({ position: 'fixed', margin: 0, x: 0, y: 0, zIndex: 999 }, box()));
  }

  function open() {
    if (isOpen) {
      return;
    }

    isOpen = true;
    inlineStyle = stage.getAttribute('style') || '';

    load();
    lockScroll();

    // Meten waar de kaart nu staat, dan pas verhuizen -- Flip animeert het verschil.
    const state = Flip.getState(stage);

    document.body.appendChild(stage);
    stage.dataset.showreelState = 'open';
    lightbox.dataset.showreelState = 'open';
    place();
    start();

    Flip.from(state, {
      duration: reduced ? 0 : DURATION,
      ease: EASE,
      absolute: true,
      scale: false,
    });

    close.removeAttribute('tabindex');
    close.focus({ preventScroll: true });
  }

  function shut() {
    if (! isOpen) {
      return;
    }

    isOpen = false;

    if (player) {
      player.stop();
    }

    const state = Flip.getState(stage);

    slot.appendChild(stage);
    stage.setAttribute('style', inlineStyle);
    stage.dataset.showreelState = 'closed';
    lightbox.dataset.showreelState = 'closed';

    Flip.from(state, {
      duration: reduced ? 0 : DURATION,
      ease: EASE,
      absolute: true,
      scale: false,
      onComplete: () => {
        // Pas leeghalen als hij terug is: een iframe die halverwege verdwijnt laat een
        // gat achter, en een die blijft staan speelt onzichtbaar door.
        media.innerHTML = '';
        player = null;
        unlockScroll();

        if (preview) {
          preview.play().catch(() => {});
        }
      },
    });

    close.setAttribute('tabindex', '-1');
    trigger.focus({ preventScroll: true });
  }

  /**
   * De film uit de <template> halen. Gebeurt bij elke opening opnieuw, zodat een iframe
   * met autoplay ook de tweede keer gewoon begint.
   */
  function load() {
    media.innerHTML = '';
    media.appendChild(source.content.cloneNode(true));
    player = setupPlayer(media.querySelector('[data-player-init]'));

    if (preview) {
      preview.pause();
    }
  }

  /**
   * Beginnen met spelen. Staat los van het inladen: dit gebeurt pas als de speler ook
   * echt zichtbaar is, want een browser start geen video in een verborgen element.
   */
  function start() {
    if (player) {
      player.play();

      return;
    }

    const video = media.querySelector('video');

    if (video) {
      video.play().catch(() => {});
    }
  }


}
